import { Navigate, Route, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { lazy, useEffect } from "react";
import { useSelector } from "react-redux";
import { featureKeys } from "../../../../../src/utils/features";
import { storeClassroomFirstLandingPage } from "../../../../../src/utils/classroomFirstLandingPage";
import {
  localeRedirects,
  RequiresLogin,
  SentryRoutes,
  suspense,
} from "../../../../../src/components/AppRoutes";
import UnableToAccess from "../../../../../src/components/UnableToAccess/UnableToAccess";
import PageLoadingSpinner from "../SchoolSettings/PageLoadingSpinner";
import { classroomOidcAuthKey as authKey } from "../../utils/oidcConfig";
import { actionLoadingStates } from "../../../../../src/utils/stateEnums";

export const BLOCKS_PATH = "blocks";

const Callback = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/Callback"
    ),
);
const UserLogin = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/UserLogin"
    ),
);
const GoogleOAuthCallback = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/GoogleOAuthCallback"
    ),
);
const SilentRenew = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/SilentRenew/SilentRenew"
    ),
);
const FrontChannelLogout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/FrontChannelLogout/FrontChannelLogout"
    ),
);
const UserTypeLogin = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/UserTypeLogin/UserTypeLogin"
    ),
);
const GlobalLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/GlobalLayout/GlobalLayout"
    ),
);

const EmbeddedViewer = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/EmbeddedViewer/EmbeddedViewer"
    ),
);

const SchoolOnboarding = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/SchoolOnboarding/SchoolOnboarding"
    ),
);
const SchoolDashboard = lazy(
  () =>
    import(/* webpackPrefetch: true */ "../../pages/School/SchoolDashboard"),
);

const SchoolSettingsLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../SchoolSettings/SchoolSettingsLayout"
    ),
);

const SchoolSettingsDetails = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../SchoolSettings/SchoolSettingsDetails"
    ),
);

const SchoolSettingsFeatures = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../SchoolSettings/SchoolSettingsFeatures"
    ),
);

const SchoolSettingsSsoForStudents = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../SchoolSettings/SchoolSettingsSsoForStudents"
    ),
);

const SchoolOwnership = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../pages/SchoolSettings/SchoolOwnership"
    ),
);

const SchoolLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/SchoolLayout/SchoolLayout"
    ),
);

const AuthenticatedSchoolOnboarding = RequiresLogin(SchoolOnboarding);
const AuthenticatedSchoolLayout = RequiresLogin(SchoolLayout);

const MembersPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/MembersPage/MembersPage"
    ),
);

const ClassLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ClassLayout/ClassLayout"
    ),
);

const ClassPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ClassPage/ClassPage"
    ),
);

const ClassMembersPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ClassMembersPage/ClassMembersPage"
    ),
);

const ClassImporter = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ClassImporter/ClassImporter"
    ),
);

const LessonPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/LessonPage/LessonPage"
    ),
);

const ProjectPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ProjectPage/ProjectPage"
    ),
);

const ProjectPreviewPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ProjectPreviewPage/ProjectPreviewPage"
    ),
);

const InvitationStart = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/Invitation/Start"
    ),
);

const InvitationResponsibilities = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/Invitation/Responsibilities"
    ),
);

const InvitationConfirmation = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/Invitation/Confirmation"
    ),
);

const InterstitialPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/InterstitialPage/InterstitialPage"
    ),
);

const BlocksEditorLandingPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/BlocksEditorLandingPage/BlocksEditorLandingPage"
    ),
);

const ClassroomLandingPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ClassroomLandingPage/ClassroomLandingPage"
    ),
);

const ChooseRole = lazy(
  () => import(/* webpackPrefetch: true */ "../../pages/JoinClass/ChooseRole"),
);

const Confirm = lazy(
  () => import(/* webpackPrefetch: true */ "../../pages/JoinClass/Confirm"),
);

const JoinRedirect = () => {
  const { joinCode } = useParams();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return <Navigate replace to={`/${locale}/join/${joinCode}`} />;
};

export const ClassroomAppRoutes = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const featuresLoading = useSelector((state) => state.features?.loading);
  const enabledFeatures = useSelector((state) => state.features?.enabled ?? []);
  const areFeaturesLoaded =
    featuresLoading === actionLoadingStates.succeeded ||
    featuresLoading === actionLoadingStates.failed;
  const locale = i18n.language;

  useEffect(() => {
    storeClassroomFirstLandingPage(location);
  }, [location]);

  const featureFlaggedRoute = (flag, targetComponent, fallbackRoute) => {
    return !areFeaturesLoaded ? (
      <PageLoadingSpinner />
    ) : enabledFeatures.includes(flag) ? (
      suspense(targetComponent)
    ) : (
      <Navigate to={fallbackRoute} replace />
    );
  };

  return (
    <SentryRoutes>
      <Route path="auth">
        <Route path="callback" element={suspense(<Callback />)} />
        <Route path="silent_renew" element={suspense(<SilentRenew />)} />
        <Route
          path="frontchannel_logout"
          element={suspense(<FrontChannelLogout />)}
        />
        <Route path="user_login/:userType" element={suspense(<UserLogin />)} />
        <Route
          path="google/callback"
          element={suspense(<GoogleOAuthCallback />)}
        />
      </Route>

      <Route
        path={":locale"}
        element={suspense(<GlobalLayout authKey={authKey} />)}
      >
        <Route path="login" element={suspense(<UserTypeLogin />)} />
        <Route index element={suspense(<ClassroomLandingPage />)} />
        <Route path="education" element={<Navigate to=".." replace />} />
        <Route path="*" element={suspense(<UnableToAccess />)} />
        <Route path="error" element={suspense(<UnableToAccess />)} />

        <Route
          path="embed/viewer/:identifier"
          element={suspense(<EmbeddedViewer authKey={authKey} />)}
        />

        <Route
          path="preview/projects/:identifier"
          element={suspense(<ProjectPreviewPage />)}
        />

        <Route
          path="schools/new/:userOrigin?/:originUrl?"
          element={suspense(<AuthenticatedSchoolOnboarding />)}
        />
        <Route
          path="invitations/:token"
          element={suspense(<InvitationStart />)}
        />
        <Route path="lesson" element={suspense(<LessonPage />)} />
        <Route
          path="invitations/:token/responsibilities"
          element={suspense(<InvitationResponsibilities />)}
        />
        <Route
          path="invitations/:token/confirmation"
          element={suspense(<InvitationConfirmation />)}
        />

        <Route path="school" element={suspense(<AuthenticatedSchoolLayout />)}>
          <Route path=":schoolCode" element={suspense(<SchoolDashboard />)} />
          <Route index element={suspense(<SchoolDashboard />)} />
          <Route path="members" element={suspense(<MembersPage />)} />
          <Route path="settings" element={suspense(<SchoolSettingsLayout />)}>
            <Route index element={suspense(<SchoolSettingsDetails />)} />
            <Route
              path="features"
              element={suspense(<SchoolSettingsFeatures />)}
            />
            <Route
              path="ssoForStudents"
              element={suspense(<SchoolSettingsSsoForStudents />)}
            />
            <Route
              path="schoolownership"
              element={featureFlaggedRoute(
                featureKeys.TRANSFER_OWNERSHIP,
                <SchoolOwnership />,
                "..",
              )}
            />
          </Route>
          <Route path="class/importer" element={suspense(<ClassImporter />)} />
          <Route path="class" element={suspense(<ClassLayout />)}>
            <Route index element={suspense(<ClassPage />)} />
            <Route path="members" element={suspense(<ClassMembersPage />)} />
          </Route>
          <Route path="lesson" element={suspense(<LessonPage />)} />
          <Route
            path="project"
            element={suspense(<ProjectPage authKey={authKey} />)}
          >
            <Route
              path=":identifier"
              element={suspense(<ProjectPage authKey={authKey} />)}
            />
          </Route>
          <Route
            path=":schoolCode/class/:classCode"
            element={suspense(<ClassLayout />)}
          >
            <Route index element={suspense(<ClassPage />)} />
          </Route>
        </Route>

        <Route
          path="create-account-info/:userOrigin?/:originUrl?"
          element={suspense(<InterstitialPage />)}
        />

        <Route path="join/:joinCode" element={suspense(<ChooseRole />)} />

        <Route path="join/:joinCode/confirm" element={suspense(<Confirm />)} />

        <Route
          path={BLOCKS_PATH}
          element={suspense(<BlocksEditorLandingPage />)}
        />
      </Route>

      <Route path="join/:joinCode" element={<JoinRedirect />} />

      {localeRedirects.map((link) => {
        return (
          <Route
            key={link}
            path={link}
            element={<Navigate replace to={`/${locale}${link}`} />}
          />
        );
      })}
    </SentryRoutes>
  );
};
